// Pure geometry helpers, style constants, and zoom-based style calculators
// for the world map. No React, no Leaflet dependency — safe to import from
// any module.

// Black & white theme: dark = black water / white continents; light = white
// water / gray continents. Visited countries are always yellow.
export const VISITED_STYLE = {
  fillColor: '#facc15',
  color: '#ca8a04',
  weight: 0.6,
  fillOpacity: 1,
};
export const DEFAULT_DARK = {
  fillColor: '#383838',
  color: '#909090',
  weight: 2,
  fillOpacity: 1,
};
export const DEFAULT_LIGHT = {
  fillColor: '#e8e8e8',
  color: '#a0a0a0',
  weight: 2,
  fillOpacity: 1,
};
export const WATER_DARK = '#0b161c';
export const WATER_LIGHT = '#f0f0f0';

// Single color used for all past navigation routes.
export const ROUTE_COLOR = '#3b82f6';
export const PATH_COLORS = { driving: '#3b82f6', foot: '#ef4444', bike: '#22c55e' };

// Bounding box of any coordinate tree (geometry.coordinates, a single ring,
// or a polygon's outer ring). Walks recursively — handles Polygon, MultiPolygon,
// and raw ring arrays. Returns { minLat, maxLat, minLng, maxLng }.
export function computeBbox(coords) {
  let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
  const walk = (c) => {
    if (typeof c[0] === 'number') {
      const [lng, lat] = c;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      return;
    }
    for (const x of c) walk(x);
  };
  walk(coords);
  return { minLat, maxLat, minLng, maxLng };
}

// Progressive border weight: base at zoom ≤ 4, then 1.5× thicker per zoom
// step from zoom 6 onwards (6→1.5×, 8→2.25×, 10→3.375×, 12→5.06×).
export function borderWeightMultiplier(zoom) {
  if (zoom <= 4) return 1;
  const steps = (zoom - 4) / 2;
  return Math.pow(1.5, steps);
}

// Requested zoom behavior: soften visited fills once admin-1 region borders
// become visible, so those borders remain readable over the country fill.
export function fillOpacityFor(zoom, isVisited) {
  // Keep the base map's land fill visible; only visited areas soften at
  // close zoom so the admin-1 overlay can remain readable.
  if (!isVisited) return 1;
  if (zoom < 4) return 1;
  return 0.25;
}

// From zoom 8+ borders are pure white (dark) / pure black (light);
// below zoom 8 the default grey from the style constants is used.
export function borderColorFor() {
  // Basemap tiles already draw country borders, so the vector border layer is
  // fully transparent to avoid doubling them.
  return 'transparent';
}

// Area-weighted centroid of a polygon's outer ring (x = lng, y = lat).
function ringCentroid(ring) {
  let a = 0, cx = 0, cy = 0;
  for (let i = 0, n = ring.length - 1; i < n; i++) {
    const [x0, y0] = ring[i];
    const [x1, y1] = ring[i + 1];
    const cross = x0 * y1 - x1 * y0;
    a += cross;
    cx += (x0 + x1) * cross;
    cy += (y0 + y1) * cross;
  }
  a *= 0.5;
  if (Math.abs(a) < 1e-9) {
    let sx = 0, sy = 0;
    for (const [x, y] of ring) { sx += x; sy += y; }
    return [sy / ring.length, sx / ring.length];
  }
  return [cy / (6 * a), cx / (6 * a)];
}

// Centroid of a feature's largest polygon, so elongated countries (Norway)
// and antimeridian-spanning ones (Russia, Alaska) anchor at a sensible spot
// instead of a naive bbox centre or longitude 0. Rings that span the
// antimeridian (longitudes on both sides of ±180°, e.g. Alaska + Aleutians)
// are unwrapped by shifting negative longitudes to +360 before computing the
// centroid, so the result lands in the mainland instead of the Pacific.
export function featureCenter(feature) {
  const polys =
    feature.geometry.type === 'Polygon'
      ? [feature.geometry.coordinates]
      : feature.geometry.coordinates;
  let bestArea = -1;
  let bestRing = null;
  for (const poly of polys) {
    const ring = poly[0];
    const { minLat, maxLat, minLng, maxLng } = computeBbox(ring);
    const span = maxLng - minLng;
    let area;
    let useRing = ring;
    if (span > 180) {
      // Ring wraps the antimeridian — shift negative longitudes to +360 so
      // the ring is continuous, then compute the centroid in unwrapped space.
      useRing = ring.map(([lng, lat]) => [lng < 0 ? lng + 360 : lng, lat]);
      // Recompute span after shifting
      let nMin = 360, nMax = -360;
      for (const [lng] of useRing) { if (lng < nMin) nMin = lng; if (lng > nMax) nMax = lng; }
      area = (maxLat - minLat) * (nMax - nMin);
    } else {
      area = (maxLat - minLat) * span;
    }
    if (area > bestArea) { bestArea = area; bestRing = useRing; }
  }
  if (!bestRing) bestRing = polys[0][0];
  const [clat, clng] = ringCentroid(bestRing);
  // Normalize longitude back to [-180, 180]
  const normLng = ((clng + 180) % 360 + 360) % 360 - 180;
  return [clat, normLng];
}

// Point-in-polygon test (ray casting). Handles Polygon and MultiPolygon.
export function pointInPolygon(lat, lng, geometry) {
  const x = lng, y = lat;
  const checkRing = (ring) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };
  if (geometry.type === 'Polygon') return checkRing(geometry.coordinates[0]);
  if (geometry.type === 'MultiPolygon') return geometry.coordinates.some((p) => checkRing(p[0]));
  return false;
}